import { useAuth } from '../context/AuthContext'
import { useGame } from '../context/GameContext'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Flame, Trophy, Medal, ArrowLeft, Users } from 'lucide-react'
import { clsx } from 'clsx'
import { useEffect, useState } from 'react'
import { LeaderboardEntry } from '../context/GameContext'

export default function Leaderboard() {
  const { user } = useAuth()
  const { fetchLeaderboard, leaderboard, loading } = useGame()
  const [timeframe, setTimeframe] = useState<'daily' | 'weekly' | 'alltime'>('weekly')

  useEffect(() => {
    fetchLeaderboard()
  }, [fetchLeaderboard, timeframe])

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 lg:py-10 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => window.location.href = '/'}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <p className="text-sm text-surface-500 dark:text-surface-400">Leaderboard</p>
            <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100">Weekly Leaders</h1>
          </div>
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-surface-200 dark:bg-surface-700" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-surface-200 dark:bg-surface-700 rounded w-3/4" />
                    <div className="h-3 bg-surface-200 dark:bg-surface-700 rounded w-1/2" />
                  </div>
                  <div className="w-16 h-8 bg-surface-200 dark:bg-surface-700 rounded" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 lg:py-10 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Button variant="ghost" size="sm" onClick={() => window.location.href = '/'}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <p className="text-sm text-surface-500 dark:text-surface-400">Leaderboard</p>
          <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100">Weekly Leaders</h1>
        </div>
      </div>

      {/* Timeframe Selector */}
      <div className="flex items-center gap-2">
        <select
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value as 'daily' | 'weekly' | 'alltime')}
          className="input-field w-auto"
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="alltime">All Time</option>
        </select>
      </div>

      {/* Current User Rank Card */}
      {leaderboard?.userRank && leaderboard.userRank > 0 && leaderboard && (
        <Card className="bg-gradient-to-r from-brand-50 to-brand-100 dark:from-brand-900/30 dark:to-brand-800/30 border-brand-200 dark:border-brand-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white font-black text-xl">
                  #{leaderboard.userRank}
                </div>
                <div>
                  <p className="text-sm text-brand-700 dark:text-brand-300 font-medium">Your Rank</p>
                  <p className="text-2xl font-black text-brand-900 dark:text-brand-100">#{leaderboard.userRank}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-brand-700 dark:text-brand-300">Your Score</p>
                <p className="text-2xl font-black text-brand-900 dark:text-brand-100">{leaderboard.userScore || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard */}
      <div className="space-y-3">
        {leaderboard?.players?.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center py-8">
              <Trophy className="w-16 h-16 mx-auto mb-4 text-surface-300 dark:text-surface-600" />
              <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-2">No scores yet</h3>
              <p className="text-surface-500 dark:text-surface-400">Be the first to play and claim the top spot!</p>
              <Button onClick={() => window.location.href = '/'} className="mt-4">
                <Flame className="w-4 h-4 mr-2" />
                Play Daily Game
              </Button>
            </CardContent>
          </Card>
        ) : (
          leaderboard?.players?.map((player, index) => (
            <Card key={player.user_id} className={clsx(
              player.user_id === user?.id && 'ring-2 ring-brand-500 bg-brand-50 dark:bg-brand-900/20'
            )}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-4">
                  {/* Rank */}
                  <div className={clsx(
                    'w-12 h-12 rounded-xl flex items-center justify-center font-black text-white flex-shrink-0',
                    index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-500' :
                    index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-500' :
                    index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700' :
                    'bg-gradient-to-br from-surface-300 to-surface-400 dark:from-surface-600 dark:to-surface-700'
                  )}>
                    {index < 3 ? (
                      index === 0 ? <Trophy className="w-6 h-6" /> :
                      index === 1 ? <Medal className="w-6 h-6" /> :
                      <Medal className="w-6 h-6" />
                    ) : (
                      <span className="font-black text-lg">{index + 1}</span>
                    )}
                  </div>

                  {/* Player Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white font-bold">
                        {player.display_name?.[0]?.toUpperCase() || player.username?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-semibold text-surface-900 dark:text-surface-100 truncate">{player.display_name || player.username || 'Player'}</p>
                        <p className="text-xs text-surface-500 dark:text-surface-400">@{player.username}</p>
                      </div>
                    </div>
                  </div>

                  {/* Score */}
                  <div className="text-right">
                    <p className="text-2xl font-black text-brand-600 dark:text-brand-400">{player.score}</p>
                    <p className="text-xs text-surface-500 dark:text-surface-400">points</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Category Leaders */}
      <div className="grid grid-cols-3 gap-4">
        <CategoryLeaderCard
          icon={Trophy}
          title="Highest Score"
          leader={leaderboard?.topScore}
          color="brand"
        />
        <CategoryLeaderCard
          icon={Flame}
          title="Longest Streak"
          leader={leaderboard?.topStreak}
          color="orange"
        />
        <CategoryLeaderCard
          icon={Users}
          title="Most Games"
          leader={leaderboard?.topDuels}
          color="purple"
        />
      </div>
    </div>
  )
}

interface CategoryLeaderCardProps {
  icon: React.ComponentType<{ className?: string }>
  title: string
  leader?: LeaderboardEntry | null
  color: 'brand' | 'orange' | 'purple'
}

function CategoryLeaderCard({ icon: Icon, title, leader, color }: CategoryLeaderCardProps) {
  const colors = {
    brand: 'bg-brand-500',
    orange: 'bg-orange-500',
    purple: 'bg-purple-500',
  }

  return (
    <Card className="text-center">
      <CardContent className="py-6">
        <div className={clsx('w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center', colors[color])}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <p className="text-sm text-surface-500 dark:text-surface-400 mb-1">{title}</p>
        {leader ? (
          <>
            <p className="text-2xl font-black text-surface-900 dark:text-surface-100">{leader.score}</p>
            <p className="text-sm text-surface-500 dark:text-surface-400">@{leader.username}</p>
            <p className="text-xs text-surface-400">{leader.display_name}</p>
          </>
        ) : (
          <p className="text-surface-400">No data yet</p>
        )}
      </CardContent>
    </Card>
  )
}